import React, { createContext, useContext, useState, ReactNode } from 'react';

interface PublishedSlide {
    id: string;
    start: number;
    [key: string]: any;
}

interface PresenterState {
    showPresenterModule: boolean;
    showPresenterOverlayModule: boolean;
    showPresenterOverlayModuleFullscreen: boolean;
    presentationPublishedSlides: PublishedSlide[];
}

interface PresenterContextType extends PresenterState {
    setShowPresenterModule: (_show: boolean) => void;
    setShowPresenterOverlayModule: (_show: boolean) => void;
    setShowPresenterOverlayModuleFullscreen: (_show: boolean) => void;
    addPresentationPublishedSlide: (_slide: PublishedSlide) => void;
    setPresentationPublishedSlides: (_slides: PublishedSlide[]) => void;
}

const initialState: PresenterState = {
    showPresenterModule: false,
    showPresenterOverlayModule: false,
    showPresenterOverlayModuleFullscreen: false,
    presentationPublishedSlides: [],
};

const PresenterContext = createContext<PresenterContextType | undefined>(undefined);

export const PresenterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<PresenterState>(initialState);

    const setShowPresenterModule = (showPresenterModule: boolean) => {
        if (showPresenterModule) {
            setState((s) => ({
                ...s,
                showPresenterModule,
                showTranscriptionsModule: false, // Ensure presenter module hides transcription module
            }));
        } else {
            setState((s) => ({ ...s, showPresenterModule }));
        }
    };

    const setShowPresenterOverlayModule = (showPresenterOverlayModule: boolean) =>
        setState((s) => ({ ...s, showPresenterOverlayModule }));

    const setShowPresenterOverlayModuleFullscreen = (showPresenterOverlayModuleFullscreen: boolean) =>
        setState((s) => ({ ...s, showPresenterOverlayModuleFullscreen }));

    const addPresentationPublishedSlide = (currentPublishedSlide: PublishedSlide) => {
        setState((s) => {
            const exists = s.presentationPublishedSlides.find((slide) => slide.id === currentPublishedSlide.id);
            let newSlides = s.presentationPublishedSlides.map((slide) => {
                if (slide.id === currentPublishedSlide.id) {
                    return currentPublishedSlide;
                }
                return slide;
            });
            newSlides = exists ? newSlides : [...(newSlides ?? []), currentPublishedSlide];
            return {
                ...s,
                presentationPublishedSlides: newSlides.sort((a, b) => a.start - b.start),
            };
        });
    };

    const setPresentationPublishedSlides = (presentationPublishedSlides: PublishedSlide[]) =>
        setState((s) => ({ ...s, presentationPublishedSlides }));

    const value: PresenterContextType = {
        ...state,
        setShowPresenterModule,
        setShowPresenterOverlayModule,
        setShowPresenterOverlayModuleFullscreen,
        addPresentationPublishedSlide,
        setPresentationPublishedSlides,
    };

    return <PresenterContext.Provider value={value}>{children}</PresenterContext.Provider>;
};

export const usePresenter = (): PresenterContextType => {
    const context = useContext(PresenterContext);
    if (!context) {
        throw new Error('usePresenter must be used within a PresenterProvider');
    }
    return context;
};
